import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Shield, Mail, FileText } from "lucide-react";
import { useSeo } from "@/lib/seo";
import { COMPANY_ADDRESS, COMPANY_NAME, COMPANY_PEC, COMPANY_VAT, SUPPORT_EMAIL } from "@/lib/company";

export default function Privacy() {
    useSeo({ title: "Informativa Privacy GDPR | VendiDirect", description: "Come VendiDirect tratta i tuoi dati personali: finalità, base giuridica, conservazione, diritti dell'interessato e contatti del titolare (GDPR).", canonical: "/privacy" });
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div data-testid="privacy-page" className="pt-32 pb-24 max-w-3xl mx-auto px-6 lg:px-12">
            <div className="flex items-center gap-2 mb-3">
                <Shield size={14} className="text-gold" />
                <span className="label-eyebrow">Conformità GDPR</span>
            </div>
            <h1 className="font-serif text-4xl sm:text-5xl text-ink-50 leading-tight">Informativa sulla privacy</h1>
            <p className="text-ink-400 text-sm mt-4">Ultimo aggiornamento: 15 settembre 2026 · Versione 1.3</p>

            <div className="mt-10 p-5 border border-gold/30 bg-gold/5 text-sm text-ink-100 leading-relaxed">
                Questa informativa è redatta ai sensi del <strong>Regolamento UE 2016/679 (GDPR)</strong> e del D.Lgs. 196/2003 come modificato dal D.Lgs. 101/2018. Descrive come vendidirect.it raccoglie, utilizza e protegge i tuoi dati personali.
            </div>

            <Section title="1. Titolare del trattamento">
                <p>
                    Il Titolare del trattamento è <strong>{COMPANY_NAME}</strong> (di seguito "vendidirect" o "noi").
                </p>
                <ul className="mt-3 space-y-1">
                    <li>Sede Legale: {COMPANY_ADDRESS}</li>
                    <li>P.IVA / C.F.: {COMPANY_VAT}</li>
                    <li>PEC: <a href={`mailto:${COMPANY_PEC}`} className="text-gold hover:underline">{COMPANY_PEC}</a></li>
                    <li>Email di riferimento privacy e supporto: <a href={`mailto:${SUPPORT_EMAIL}`} className="text-gold hover:underline">{SUPPORT_EMAIL}</a></li>
                </ul>
                <p className="mt-3 text-xs text-ink-400">
                    Non è obbligatoria la nomina di un DPO (Data Protection Officer) per la nostra attuale dimensione e tipologia di trattamento; tuttavia, qualsiasi richiesta in materia di privacy può essere inviata all'email di riferimento.
                </p>
            </Section>

            <Section title="2. Tipologie di dati raccolti">
                <p>vendidirect raccoglie e tratta le seguenti categorie di dati personali:</p>
                <div className="mt-4 space-y-3">
                    <DataBlock label="Dati di registrazione e profilo" items={["Email", "Nome e cognome", "Password conservata esclusivamente in forma cifrata e non reversibile", "Ruolo (privato/agenzia/collaboratore)", "Numero di telefono (facoltativo)", "Dati agenzia: ragione sociale, logo, descrizione (solo agenzie)"]} />
                    <DataBlock label="Dati di fatturazione" items={["Indirizzo fiscale", "Partita IVA / Codice Fiscale", "Ragione sociale", "Paese (per IVA EU)"]} />
                    <DataBlock label="Dati di pagamento" items={["Importo, data, stato di ogni transazione", "ID transazione Stripe (i numeri di carta NON sono mai memorizzati da noi)"]} />
                    <DataBlock label="Dati degli annunci pubblicati" items={["Titolo, descrizione, prezzo", "Foto immobile (immagini caricate dall'utente)", "Indirizzo dell'immobile (visibile solo dopo consenso esplicito proprietario)", "Caratteristiche tecniche (mq, locali, classe energetica)"]} />
                    <DataBlock label="Dati di interazione" items={["Messaggi nella chat interna", "Richieste di contatto e consenso", "Appuntamenti per visite", "Annunci preferiti", "Ricerche salvate"]} />
                    <DataBlock label="Dati tecnici" items={["Indirizzo IP", "Tipo di browser e dispositivo", "Pagine visitate, durata sessione", "Cookie tecnici e analitici (con consenso)"]} />
                </div>
            </Section>

            <Section title="3. Finalità e base giuridica del trattamento">
                <p>I tuoi dati vengono trattati per le seguenti finalità (art. 6 GDPR):</p>
                <div className="mt-4 space-y-3 text-sm">
                    <Purpose finalita="Erogazione del servizio (login, pubblicazione annunci, chat, prenotazione visite)" base="Esecuzione di un contratto (art. 6.1.b)" />
                    <Purpose finalita="Fatturazione e adempimenti fiscali" base="Obbligo legale (art. 6.1.c) — DPR 633/72, DPR 600/73" />
                    <Purpose finalita="Creazione delle fatture elettroniche in Fattura24 dopo un pagamento Stripe riuscito" base="Esecuzione di un contratto + obbligo legale (art. 6.1.b/c)" />
                    <Purpose finalita="Invio di email transazionali (conferma, promemoria visite, sblocco contatti)" base="Esecuzione di un contratto (art. 6.1.b)" />
                    <Purpose finalita="Invio email marketing e newsletter" base="Consenso (art. 6.1.a) — revocabile in qualsiasi momento" />
                    <Purpose finalita="Distribuzione annunci su portali esterni (Idealista, Casa.it, Immobiliare.it)" base="Consenso esplicito agenzia (art. 6.1.a)" />
                    <Purpose finalita="Statistiche aggregate e VD Manager" base="Interesse legittimo (art. 6.1.f) — dati pseudonimizzati" />
                    <Purpose finalita="Prevenzione frodi e sicurezza" base="Interesse legittimo (art. 6.1.f)" />
                    <Purpose finalita="Audit log azioni admin" base="Obbligo legale + interesse legittimo (art. 6.1.c/f)" />
                </div>
            </Section>

            <Section title="4. Privacy by Design — Sistema di consenso esplicito">
                <p>
                    vendidirect è progettato secondo il principio di <strong>privacy by design</strong> (art. 25 GDPR). In particolare:
                </p>
                <ul className="mt-3 space-y-2 list-disc ml-6">
                    <li><strong>Email e telefono dei proprietari sono privati di default</strong>. Non vengono mai mostrati pubblicamente.</li>
                    <li>Un utente abbonato deve <strong>richiedere il consenso esplicito</strong> al proprietario per: (a) iniziare una chat, (b) sbloccare i contatti.</li>
                    <li>Il proprietario riceve un'email con un link sicuro <strong>token-based</strong> per accettare o rifiutare la richiesta.</li>
                    <li>Senza consenso esplicito, <strong>nessun dato di contatto viene mai condiviso</strong>, neanche dopo pagamento di un abbonamento.</li>
                    <li>L'indirizzo esatto dell'immobile è offuscato fino al consenso (viene mostrata solo città/regione).</li>
                </ul>
            </Section>

            <Section title="5. Destinatari dei dati (sub-responsabili del trattamento)">
                <p>Per erogare il servizio, vendidirect condivide alcuni dati con i seguenti fornitori, tutti conformi GDPR:</p>
                <div className="mt-4 overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead className="text-ink-400 uppercase tracking-wider border-b border-white/10">
                            <tr>
                                <th className="text-left py-2 pr-3">Fornitore</th>
                                <th className="text-left py-2 pr-3">Servizio</th>
                                <th className="text-left py-2 pr-3">Dati condivisi</th>
                                <th className="text-left py-2">Sede</th>
                            </tr>
                        </thead>
                        <tbody className="text-ink-100 divide-y divide-white/5">
                            <Row a="Stripe Payments Europe Ltd" b="Pagamenti" c="Email, importi, dati fatturazione" d="Irlanda (UE)" />
                            <Row a="Resend Inc." b="Email transazionali" c="Email, contenuto messaggio" d="USA (DPF certified)" />
                            <Row a="Fattura24 (Fattura24.com)" b="Fatturazione elettronica" c="Ragione sociale, P.IVA/C.F., indirizzo, email, importo e servizio acquistato" d="Italia (UE)" />
                            <Row a="OpenAI / Anthropic / Google AI" b="Chatbot AI" c="Solo testo del messaggio (no PII)" d="USA (DPF certified)" />
                            <Row a="OpenStreetMap Foundation" b="Mappe" c="Solo coordinate GPS pubbliche" d="UK/UE" />
                            <Row a="Idealista / Casa.it / Immobiliare.it" b="Pubblicazione annunci (solo se attivato dall'agenzia)" c="Dati pubblici annuncio" d="UE" />
                            <Row a="MongoDB (self-hosted)" b="Database" c="Tutti i dati applicativi" d="UE" />
                        </tbody>
                    </table>
                </div>
                <p className="text-xs text-ink-400 mt-4">
                    Per i fornitori extra-UE indicati come "DPF certified", il trasferimento avviene secondo il Data Privacy Framework UE-USA (Decisione di adeguatezza UE 2023/1795).
                </p>
            </Section>

            <Section title="6. Periodo di conservazione">
                <ul className="space-y-2 list-disc ml-6">
                    <li><strong>Dati account</strong>: per tutta la durata dell'iscrizione, più 30 giorni dopo la cancellazione (per recupero)</li>
                    <li><strong>Fatture e dati fiscali</strong>: <strong>10 anni</strong> (obbligo art. 2220 c.c. e DPR 600/73)</li>
                    <li><strong>Annunci pubblicati</strong>: per tutta la durata + 12 mesi dopo la rimozione (per audit)</li>
                    <li><strong>Messaggi chat</strong>: 24 mesi dall'ultima interazione</li>
                    <li><strong>Log di accesso e audit</strong>: 12 mesi</li>
                    <li><strong>Backup database</strong>: 30 giorni</li>
                    <li><strong>Cookie tecnici</strong>: sessione (alcuni 7 giorni)</li>
                    <li><strong>Cookie analitici</strong>: 12 mesi (solo se accettati)</li>
                </ul>
            </Section>

            <Section title="7. I tuoi diritti">
                <p>Ai sensi degli articoli 15-22 GDPR hai i seguenti diritti, esercitabili gratuitamente:</p>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <RightCard title="Accesso (art. 15)" desc="Sapere quali dati abbiamo su di te e ottenerne una copia" />
                    <RightCard title="Rettifica (art. 16)" desc="Correggere dati inesatti o incompleti" />
                    <RightCard title="Cancellazione (art. 17)" desc="Diritto all'oblio: cancellare il tuo account e tutti i dati associati (esclusi obblighi fiscali)" />
                    <RightCard title="Limitazione (art. 18)" desc="Bloccare temporaneamente il trattamento di alcuni dati" />
                    <RightCard title="Portabilità (art. 20)" desc="Ricevere i tuoi dati in formato strutturato (JSON/CSV) e trasferirli altrove" />
                    <RightCard title="Opposizione (art. 21)" desc="Opporti a trattamenti basati su interesse legittimo (es. marketing)" />
                    <RightCard title="Revoca del consenso" desc="Disattivare email marketing, sincronizzazioni esterne, cookie analitici in qualsiasi momento" />
                    <RightCard title="Reclamo Garante" desc="Presentare reclamo al Garante per la Protezione dei Dati (garanteprivacy.it)" />
                </div>
                <div className="mt-6 p-4 border border-gold/30 bg-gold/5 text-sm">
                    <strong>Come esercitare i tuoi diritti</strong>: invia un'email a <a href={`mailto:${SUPPORT_EMAIL}`} className="text-gold hover:underline">{SUPPORT_EMAIL}</a> con oggetto "Richiesta GDPR — [tipo richiesta]". Rispondiamo entro <strong>30 giorni</strong> (art. 12.3 GDPR).
                </div>
            </Section>

            <Section title="8. Sicurezza dei dati">
                <p>vendidirect applica le seguenti misure tecniche e organizzative (art. 32 GDPR):</p>
                <ul className="mt-3 space-y-2 list-disc ml-6">
                    <li>Connessione cifrata <strong>HTTPS/TLS 1.3</strong> su tutto il sito</li>
                    <li>Password e dati sensibili <strong>mai memorizzati in chiaro</strong>: le password sono protette con hashing bcrypt</li>
                    <li>Numeri di carta di credito <strong>mai trattati direttamente</strong> (delegato a Stripe, PCI-DSS Level 1)</li>
                    <li>Database con accesso ristretto e <strong>backup giornalieri cifrati</strong></li>
                    <li><strong>Audit log</strong> di ogni accesso amministrativo (impersonate, modifiche, eliminazioni)</li>
                    <li><strong>Watermark</strong> automatico sulle foto degli annunci per prevenire furti di immagini</li>
                    <li>Aggiornamenti di sicurezza applicati entro 7 giorni dalla pubblicazione</li>
                    <li>Notifica al Garante e agli interessati entro <strong>72 ore</strong> in caso di data breach (art. 33-34)</li>
                </ul>
            </Section>

            <Section title="9. Modulo Antiriciclaggio (AML) — Conservazione sicura dei documenti">
                <p>
                    Per le agenzie che utilizzano il modulo "Antiriciclaggio" del VD Manager, vendidirect fornisce
                    esclusivamente <strong>lo strumento tecnico</strong> di raccolta ed archiviazione dei dati di
                    adeguata verifica della clientela (documenti d'identità, verbali PDF, questionari di rischio),
                    richiesti dalla normativa <strong>D.Lgs. 231/2007</strong> e successive modifiche.
                </p>
                <ul className="mt-3 space-y-2 list-disc ml-6">
                    <li>Tutti i documenti caricati (carte d'identità, passaporti, ecc.) e i verbali PDF generati sono
                        cifrati a riposo con <strong>AES-256-GCM</strong>, con una chiave derivata singolarmente per
                        ciascuna agenzia ("architettura Zero Access").</li>
                    <li><strong>Nessuna rotta amministrativa o di piattaforma</strong> è in grado di derivare la
                        chiave o decifrare questi contenuti: l'accesso è possibile solo nel contesto di una richiesta
                        autenticata dall'agenzia titolare della pratica.</li>
                    <li>La <strong>responsabilità della verifica, della conservazione e della conformità normativa</strong>
                        di tali documenti rimane in ogni caso esclusivamente in capo all'Agenzia Immobiliare, in
                        quanto soggetto obbligato ai sensi del D.Lgs. 231/2007. vendidirect non effettua controlli
                        di merito sui dati inseriti né assume responsabilità sulla loro veridicità.</li>
                    <li>Il periodo di conservazione legale di riferimento per questi documenti è di
                        <strong> 10 anni</strong> dalla data dell'operazione (art. 31, D.Lgs. 231/2007); l'agenzia
                        può in ogni momento consultare ed esportare l'archivio storico delle proprie pratiche.</li>
                </ul>
            </Section>

            <Section title="10. Documenti operativi tra agenzia e cliente finale (firma grafica elettronica)">
                <p>
                    Per le agenzie, vendidirect offre uno strumento proprietario di firma grafica elettronica dei
                    documenti operativi (incarichi di vendita/locazione, proposte d'acquisto e documenti equivalenti)
                    scambiati con i propri clienti finali.
                </p>
                <ul className="mt-3 space-y-2 list-disc ml-6">
                    <li>In questo servizio vendidirect agisce esclusivamente come <strong>tramite tecnologico</strong>.</li>
                    <li>Per documentare la sottoscrizione vengono trattati immagine della firma, data e ora,
                        indirizzo IP, consenso alla clausola e impronta crittografica del file.</li>
                    <li>vendidirect <strong>NON ha accesso, non visiona e non archivia in modo consultabile</strong> i
                        contenuti dei contratti e documenti privati firmati tra l'agenzia e i propri clienti finali
                        (proprietari, acquirenti, locatari).</li>
                    <li>Tali documenti restano nella disponibilità esclusiva dell'agenzia e del cliente firmatario e
                        non sono in alcun caso accessibili al personale di vendidirect, incluso il personale
                        amministrativo con accesso al pannello di gestione della piattaforma.</li>
                    <li><strong>Clausola di non responsabilità</strong>: vendidirect declina ogni responsabilità sul
                        contenuto, la validità legale e la gestione dei documenti firmati tra l'agenzia e i propri
                        clienti finali.</li>
                </ul>
            </Section>

            <Section title="11. Cookie">
                <p>vendidirect utilizza cookie tecnici (sempre attivi) e cookie analitici (solo con consenso).</p>
                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="border border-white/10 p-4">
                        <div className="text-xs text-gold uppercase tracking-wider mb-2">Sempre attivi (tecnici)</div>
                        <ul className="text-xs space-y-1 text-ink-100">
                            <li>• session_token (autenticazione)</li>
                            <li>• cookie_consent (preferenza utente)</li>
                            <li>• cf-* (sicurezza Cloudflare)</li>
                        </ul>
                    </div>
                    <div className="border border-white/10 p-4">
                        <div className="text-xs text-ink-400 uppercase tracking-wider mb-2">Solo con consenso</div>
                        <ul className="text-xs space-y-1 text-ink-100">
                            <li>• Cookie analitici aggregati</li>
                            <li>• Preferenze interfaccia</li>
                            <li>• Cookie di profilazione: <strong>nessuno</strong></li>
                        </ul>
                    </div>
                </div>
            </Section>

            <Section title="12. Trasferimenti internazionali">
                <p>
                    La maggior parte dei dati resta in UE. Alcuni fornitori (Google, Resend, AI providers) hanno sede negli USA: in questi casi il trasferimento avviene secondo il <strong>Data Privacy Framework UE-USA</strong> (decisione di adeguatezza della Commissione UE del 10 luglio 2023) e/o clausole contrattuali standard approvate dalla Commissione UE.
                </p>
            </Section>

            <Section title="13. Minori di età">
                <p>vendidirect è riservato a utenti di almeno <strong>18 anni</strong>. Non raccogliamo consapevolmente dati di minori. Se sei genitore/tutore di un minore che ha creato un account, contattaci a <a href={`mailto:${SUPPORT_EMAIL}`} className="text-gold hover:underline">{SUPPORT_EMAIL}</a> per la rimozione immediata.</p>
            </Section>

            <Section title="14. Modifiche all'informativa">
                <p>vendidirect può aggiornare questa informativa per riflettere modifiche legali o operative. La data di "ultimo aggiornamento" in cima identifica la versione corrente. Modifiche sostanziali vengono notificate via email a tutti gli utenti registrati.</p>
                <p>VendiDirect si riserva il diritto di variare i prezzi degli abbonamenti con 30 giorni di preavviso.</p>
            </Section>

            <div className="mt-16 p-6 border border-gold/30 bg-ink-900/40 text-center">
                <Mail size={20} className="text-gold mx-auto mb-3" />
                <h3 className="font-serif text-xl text-ink-50">Hai domande sulla privacy?</h3>
                <p className="text-sm text-ink-400 mt-2">Scrivici a <a href={`mailto:${SUPPORT_EMAIL}`} className="text-gold hover:underline">{SUPPORT_EMAIL}</a></p>
                <div className="mt-4 flex justify-center gap-3 flex-wrap">
                    <Link to="/termini" className="text-xs text-ink-400 hover:text-gold underline">Termini di servizio</Link>
                    <span className="text-ink-700">·</span>
                    <Link to="/cookie" className="text-xs text-ink-400 hover:text-gold underline">Cookie policy</Link>
                </div>
            </div>
        </div>
    );
}

const Section = ({ title, children }) => (
    <section className="mt-12">
        <h2 className="font-serif text-2xl sm:text-3xl text-ink-50 mb-5 border-b border-white/10 pb-3">{title}</h2>
        <div className="text-sm text-ink-100 leading-relaxed space-y-3">{children}</div>
    </section>
);

const DataBlock = ({ label, items }) => (
    <div className="border border-white/10 p-4">
        <div className="text-xs text-gold uppercase tracking-wider mb-2">{label}</div>
        <ul className="text-xs text-ink-100 space-y-1">
            {items.map((i, k) => <li key={k}>• {i}</li>)}
        </ul>
    </div>
);

const Purpose = ({ finalita, base }) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 border-l-2 border-gold/30 pl-4 py-2">
        <div className="text-ink-100">{finalita}</div>
        <div className="text-xs text-ink-400 italic">{base}</div>
    </div>
);

const Row = ({ a, b, c, d }) => (
    <tr>
        <td className="py-2 pr-3 font-medium">{a}</td>
        <td className="py-2 pr-3 text-ink-400">{b}</td>
        <td className="py-2 pr-3 text-ink-400">{c}</td>
        <td className="py-2 text-ink-400">{d}</td>
    </tr>
);

const RightCard = ({ title, desc }) => (
    <div className="p-3 border border-white/10">
        <div className="text-xs font-medium text-gold mb-1">{title}</div>
        <div className="text-xs text-ink-100">{desc}</div>
    </div>
);
